(ns hai.web-app.sign-in
  "The sign-in page shown to anyone who is not signed in. Signing in with a new
   email or account makes the account, so this is the sign-up page too.

   Styled to match humbleai.com: Lexend headings, DM Sans text, the site's
   purple button and outline buttons, and the official Humble avatar."
  (:require
   [hai.ui.icons :as icons]
   [hai.web-app.routing :as routing]
   [lambdaisland.ornament :as o]))

(defn- sign-in-redirect-path
  "Where sign-in lands back: this page, keeping the query params routing
   forwards (e.g. `version`), so a pinned frontend survives the OAuth round
   trip. Encoded, since it rides inside the login URL's own query string."
  []
  (js/encodeURIComponent
   (routing/path-with-forwarded-query-params js/window.location.pathname)))

(defn- email-me-a-code!
  "Sends the email from the sign-in form to the one-time code flow."
  [e]
  (.preventDefault e)
  (let [email (.-value (js/document.getElementById "email"))]
    (set! js/document.location
          (str "/login/otp?email=" (js/encodeURIComponent email)
               "&redirect-path=" (sign-in-redirect-path)))))

;; Artwork

(def ^:private humble-shape
  "The Humble outline from the official icon (humbleai.com
   public/brand/humble-icon.svg), in a 71 × 71 box."
  "M55.1084 16.1448C57.4374 16.1449 59.3259 18.0325 59.3261 20.3616V50.6448C59.3261 52.974 57.4376 54.8624 55.1084 54.8625H41.5459C39.2165 54.8625 37.3281 52.9741 37.3281 50.6448V47.6135C37.328 47.2018 36.9937 46.8684 36.582 46.8684H34.207C33.7955 46.8686 33.461 47.202 33.4609 47.6135V23.1321C33.461 23.5436 33.7955 23.877 34.207 23.8772H36.582C36.9937 23.8772 37.328 23.5437 37.3281 23.1321V20.3616C37.3283 18.0324 39.2167 16.1448 41.5459 16.1448H55.1084ZM29.2431 15.9875C31.5723 15.9878 33.4599 17.8761 33.4599 20.2053V50.4875C33.4599 52.8167 31.5723 54.7051 29.2431 54.7053H15.6806C13.3513 54.7053 11.4629 52.8169 11.4629 50.4875V20.2053C11.4629 17.876 13.3513 15.9875 15.6806 15.9875H29.2431Z")

(def ^:private sparkle
  "The four-point sparkle used across humbleai.com, in a 24 × 24 box."
  "M12 0 C13 8 16 11 24 12 C16 13 13 16 12 24 C11 16 8 13 0 12 C8 11 11 8 12 0Z")

(defn- humble-logo
  "The Humble app icon: pink Humble on a rounded square of `box` colour."
  [box size]
  [:svg {:width size :height size :viewBox "0 0 71 71" :aria-hidden true}
   [:rect {:width 70.79 :height 70.79 :rx 10.58 :fill box}]
   [:path {:d humble-shape :fill "var(--colour-pink)"}]
   [:ellipse {:cx 39.79 :cy 32.03 :rx 1.69 :ry 2.46 :fill box}]
   [:ellipse {:cx 30.75 :cy 32.03 :rx 1.69 :ry 2.46 :fill box}]])

(defn- humble-avatar
  "The plain pink Humble avatar, slightly tilted, with a `shadow` under it."
  [{:keys [size tilt shadow]}]
  [:svg {:width size :height size :viewBox "0 0 100 100" :fill "none" :aria-hidden true}
   [:g {:transform (str "rotate(" tilt " 50 50)")}
    [:svg {:x 5 :y 8 :width 90 :height 85 :viewBox "9 14 53 50"}
     [:ellipse {:cx 35.4 :cy 61 :rx 22 :ry 2.4 :fill shadow}]
     [:path {:d humble-shape :fill "var(--colour-pink)"}]
     [:ellipse {:cx 39.79 :cy 32.03 :rx 1.69 :ry 2.46 :fill "var(--colour-ink)"}]
     [:ellipse {:cx 30.75 :cy 32.03 :rx 1.69 :ry 2.46 :fill "var(--colour-ink)"}]]]])

(defn- confetti
  "Still confetti: nothing moves on its own. Each piece is
   `[shape colour size left top rotation]`, placed in px."
  [pieces]
  (into [:<>]
        (for [[shape colour size left top rotation] pieces]
          [:svg {:aria-hidden true
                 :width size
                 :height size
                 :viewBox "0 0 24 24"
                 :style {:position "absolute"
                         :left left
                         :top top
                         :transform (str "rotate(" (or rotation 0) "deg)")}}
           (case shape
             :sparkle [:path {:d sparkle :fill colour}]
             :dot [:circle {:cx 12 :cy 12 :r 9 :fill colour}]
             :strip [:rect {:x 0 :y 7 :width 24 :height 11 :rx 3 :fill colour}]
             :squiggle [:path {:d "M2 15c3-8 7-8 10 0s7 8 10 0"
                               :fill "none" :stroke colour :stroke-width 4
                               :stroke-linecap "round"}])])))

(defn- shield-icon
  "The shield with a tick from the humbleai.com homepage trust section."
  []
  [:svg {:width 36 :height 36 :viewBox "0 0 60 60" :aria-hidden true}
   [:path {:d "M30 3 L54 12 V29 C54 44 44 52 30 57 C16 52 6 44 6 29 V12 Z"
           :fill "var(--colour-linen)" :stroke "#1A1530" :stroke-width 3.2 :stroke-linejoin "round"}]
   [:path {:d "M20 30 L27 37 L41 21"
           :fill "none" :stroke "#1A1530" :stroke-width 4
           :stroke-linecap "round" :stroke-linejoin "round"}]])

(def ^:private widgets
  "Each Home widget with its header colour and the icon the app uses for it."
  [{:label "Tasks" :colour "var(--tasks)" :icon [icons/humble-task]}
   {:label "Notes" :colour "var(--notes)" :icon [icons/humble-note]}
   {:label "Calendar" :colour "var(--colour-pink)" :icon [icons/calendar]}
   {:label "Contacts" :colour "var(--contact)" :icon [icons/humble-contact]}
   {:label "Timeline" :colour "var(--progress)" :icon [icons/humble-timeline]}])

(defn- new-tab-note []
  [:span.sr-only " (opens in a new tab)"])

;; Desktop: the ink panel on the left

(o/defstyled sign-in-panel :aside
  {:display "none"
   :position "relative"
   :overflow "hidden"
   :flex "1 1 50%"
   :flex-direction "column"
   :padding "48px 64px"
   :background-color "var(--colour-ink)"
   :color "var(--colour-linen)"}
  [:at-media {:min-width "64em"} {:display "flex"}]
  [:.logo {:display "flex" :align-items "center" :gap "12px"
           :font-family "'Lexend', sans-serif" :font-size "1.625rem" :font-weight 700}]
  [:.middle {:margin "auto 0" :padding "40px 0"}]
  [:.art {:position "relative" :width "340px" :height "240px" :margin-bottom "24px"}]
  [:h2 {:margin "0 0 24px"
        :color "var(--colour-linen)"
        :font-family "'Lexend', sans-serif" :font-size "2.5rem" :line-height 1.15
        :font-weight 700 :letter-spacing "-0.02em"}]
  [:.pink {:color "var(--colour-pink)"}]
  [:ul {:list-style "none" :margin 0 :padding 0
        :display "flex" :flex-wrap "wrap" :gap "10px"}]
  [:li {:display "inline-flex" :align-items "center" :gap "8px"
        :padding "8px 16px 8px 12px" :border-radius "999px"
        :color "var(--colour-ink)" :fill "currentColor" :font-size "1rem" :font-weight 600}
   [:svg {:flex "none" :width "18px" :height "18px"}]]
  [:.slogan {:border-top "1px solid var(--colour-linen-20)" :padding-top "24px"}
   [:p {:margin 0}]
   [:.line {:font-family "'Lexend', sans-serif" :font-size "1.5rem" :font-weight 700}]
   [:em {:font-family "'Atkinson Hyperlegible', sans-serif" :font-style "italic" :color "var(--colour-pink)"}]
   [:.team {:margin-top "6px" :font-size "0.9375rem" :color "var(--colour-linen-80)"}]]
  ([]
   [:<>
    [:div.logo [humble-logo "var(--colour-purple)" 44] "Humble"]
    [:div.middle
     [:div.art
      [confetti [[:sparkle "var(--colour-pink)" 20 6 40]
                 [:dot "var(--contact)" 12 44 4]
                 [:strip "var(--notes)" 18 30 150 -30]
                 [:squiggle "var(--colour-pink)" 28 280 30]
                 [:strip "var(--tasks)" 16 304 140 24]
                 [:sparkle "var(--colour-linen)" 14 268 196]
                 [:dot "var(--colour-pink)" 10 236 0]]]
      [:div {:style {:position "absolute" :left 50 :top 0}}
       [humble-avatar {:size 240 :tilt -6 :shadow "var(--colour-purple)"}]]]
     [:h2 "Your tasks, notes and people in "
      [:span.pink "one connected space."]]
     (into [:ul]
           (for [{:keys [label colour icon]} widgets]
             [:li {:style {:background-color colour}} icon label]))]
    [:div.slogan
     [:p.line "Built for minds that work " [:em "differently."]]
     [:p.team "Made by a neurodivergent team."]]]))

;; The form, on its own on mobile and on the right on desktop

(o/defstyled welcome-heading :div
  {:position "relative" :min-height "116px" :padding-right "112px"}
  [:h1 {:position "relative" :margin "14px 0 0"
        :font-family "'Lexend', sans-serif" :font-size "2rem" :line-height 1.15
        :font-weight 700 :letter-spacing "-0.02em" :color "var(--colour-ink)"}]
  [:.underlined {:position "relative" :display "inline-block"}
   [:svg {:position "absolute" :left "-4px" :bottom "-6px"}]
   [:span {:position "relative"}]]
  [:.bits {:position "absolute" :left 0 :top 0}]
  [:.avatar {:position "absolute" :right "-4px" :top "6px"}]
  ;; Desktop: no avatar (the panel has it), the heading fits on one line and
  ;; the confetti moves past its end
  [:at-media {:min-width "64em"}
   {:min-height 0 :padding "18px 0 0"}
   [:.bits {:left "180px" :top "-6px"}]
   [:.avatar {:display "none"}]]
  ([]
   [:<>
    [:div.bits
     [confetti [[:sparkle "var(--colour-purple)" 16 170 6]
                [:dot "var(--contact)" 10 204 2]
                [:strip "var(--notes)" 14 226 24 28]
                [:squiggle "var(--colour-pink)" 22 184 46]
                [:strip "var(--tasks)" 12 226 82 -24]
                [:sparkle "var(--colour-pink)" 11 196 100]]]]
    [:h1 "Welcome to "
     [:span.underlined
      [:svg {:width 132 :height 16 :viewBox "0 0 132 16" :aria-hidden true}
       [:path {:d "M3 10c30-6 70-8 126-3" :fill "none" :stroke "var(--colour-pink)"
               :stroke-width 7 :stroke-linecap "round"}]]
      [:span "Humble"]]]
    [:div.avatar [humble-avatar {:size 104 :tilt 8 :shadow "var(--colour-ink)"}]]]))

(o/defstyled sign-in-options :main
  {:flex "1 1 50%"
   :display "flex"
   :flex-direction "column"
   :box-sizing "border-box"
   :padding "24px 24px 28px"}
  [:.top-bar {:display "flex" :align-items "center" :justify-content "space-between"}]
  [:.logo {:display "flex" :align-items "center" :gap "10px"
           :font-family "'Lexend', sans-serif" :font-size "1.375rem" :font-weight 700
           :color "var(--colour-ink)"}]
  [:.back {:min-height "44px" :display "flex" :align-items "center"
           :font-size "0.9375rem" :font-weight 600 :text-decoration "underline"}]
  [:.content {:width "100%" :max-width "420px" :margin "0 auto"
              :display "flex" :flex-direction "column" :flex-grow 1}]
  [:.lede {:margin "8px 0 0" :font-size "1.0625rem" :line-height 1.5}]
  [:.providers {:display "flex" :flex-direction "column" :gap "12px" :margin-top "24px"}]
  ;; The humbleai.com outline button
  [:.provider {:min-height "52px" :box-sizing "border-box"
               :display "flex" :align-items "center" :justify-content "center"
               :border "2px solid #3A2F4C" :border-radius "999px"
               :font-size "1rem" :font-weight 600 :color "#3A2F4C" :text-decoration "none"
               :transition "background 0.2s, color 0.2s"}
   ;; A centred block as wide as the longest row ("Continue with Microsoft"),
   ;; so the logos and labels of every provider line up at any button width
   [:.inner {:display "flex" :align-items "center" :gap "8px" :width "13.6em"}]
   [:svg {:flex "none" :width "22px" :height "22px"}]
   [:&:hover {:background-color "#3A2F4C" :color "var(--colour-snow)"}]]
  [:.divider {:display "flex" :align-items "center" :gap "12px" :margin "20px 0"
              :font-size "0.9375rem" :color "#5C6168"}
   [:.line {:flex 1 :height "1px" :background-color "var(--colour-rock)"}]]
  [:form {:display "flex" :flex-direction "column"}]
  [:label {:margin-bottom "8px" :font-size "1rem" :font-weight 600 :color "var(--colour-ink)"}]
  [:input {:height "52px" :box-sizing "border-box" :padding "0 16px"
           :border "1.5px solid #8A817B" :border-radius "12px" :background-color "var(--colour-snow)"
           :font-family "inherit" :font-size "1.0625rem" :color "var(--charcoal)"}
   [:&::placeholder {:color "var(--colour-dim)" :opacity 1}]
   [:&:focus {:outline "none" :border-color "var(--colour-purple)"}]]
  ;; The purple button: it turns ink on hover, and never moves
  [:.cta {:margin "16px 0 0" :min-height "52px" :border 0 :border-radius "999px"
          :background-color "var(--colour-purple)" :color "var(--colour-snow)"
          :font-family "inherit" :font-size "1rem" :font-weight 600 :cursor "pointer"
          :transition "background 0.2s"}
   [:&:hover {:background-color "var(--colour-ink)"}]
   [:&:focus-visible {:outline "2px solid var(--colour-purple)" :outline-offset "4px"}]]
  [:.hint {:margin "10px 0 0" :font-size "0.9375rem" :color "#5C6168" :text-align "center"}]
  [:.footer {:margin-top "auto" :padding-top "24px"
             :display "flex" :flex-direction "column" :gap "14px"}]
  [:.privacy {:display "flex" :align-items "center" :gap "14px"
              :padding "12px 16px" :border-radius "12px" :background-color "var(--colour-snow)"}
   [:svg {:flex "none"}]
   [:p {:margin 0 :font-size "0.9375rem" :line-height 1.45}]]
  [:.terms {:margin 0 :font-size "0.875rem" :line-height 1.5 :color "#5C6168" :text-align "center"}]
  ;; Desktop: the ink panel shows the logo, and the form sits in the middle
  [:at-media {:min-width "64em"}
   {:padding "48px 24px"}
   [:.logo {:visibility "hidden"}]
   [:.content {:flex-grow 0 :margin "auto" :padding "32px 0"}]
   [:.footer {:margin-top "32px" :padding-top 0}]
   [:.terms [:br {:display "none"}]]]
  ([]
   [:<>
    [:div.top-bar
     [:div.logo [humble-logo "var(--colour-ink)" 36] "Humble"]
     [:a.back {:href "https://www.humbleai.com/"} "Back to site"]]
    [:div.content
     [welcome-heading]
     [:p.lede "Sign in, or make your account if you are new. You do not need a password."]
     [:div.providers
      [:a.provider
       {:href (str "/login/google?redirect-path=" (sign-in-redirect-path) "&response_type=none")
        :target "_top"}
       [:span.inner [icons/google] "Continue with Google"]]
      [:a.provider
       {:href (str "/login/microsoft?redirect-path=" (sign-in-redirect-path) "&response_type=none")
        :target "_top"}
       [:span.inner [icons/microsoft] "Continue with Microsoft"]]]
     [:div.divider [:span.line] [:span "or use your email"] [:span.line]]
     [:form {:on-submit email-me-a-code!}
      [:label {:for "email"} "Email"]
      [:input {:id "email"
               :name "email"
               :type "email"
               :required true
               :auto-complete "email"
               :placeholder "name@example.com"}]
      [:button.cta {:type "submit"} "Email me a code"]
      [:p.hint "We send a 6-digit code to your inbox."]]
     [:div.footer
      [:div.privacy
       [shield-icon]
       [:p "Your data stays in the EU. We never sell it or train AI on it."]]
      [:p.terms
       "By continuing, you agree to our" [:br] " "
       [:a {:href "https://www.humbleai.com/terms-of-service/" :target "_blank"}
        "terms" [new-tab-note]]
       " and "
       [:a {:href "https://www.humbleai.com/privacy-policy/" :target "_blank"}
        "privacy policy" [new-tab-note]]
       "."]]]]))

(o/defstyled sign-in-page :div
  {:display "flex"
   :min-height "100vh"
   :background-color "var(--colour-chalk)"
   :color "var(--charcoal)"
   :font-family "'DM Sans', sans-serif"}
  [:a {:color "var(--colour-purple)"}
   [:&:hover {:color "var(--colour-ink)"}]]
  [:.sr-only {:position "absolute" :width "1px" :height "1px" :overflow "hidden"
              :clip "rect(0 0 0 0)" :white-space "nowrap"}]
  ([]
   [:<>
    [sign-in-panel]
    [sign-in-options]]))
